/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";

import { useRouter } from "next/navigation";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Slider from "react-slick";

import { ApiError } from "@/app/common-types/api-error.types";
import UnSavedChangesWarningMessage from "@/app/shared-components/un-saved-changes-warning-message/un-saved-changes-warning-message";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import ComparisionSideDrawer from "@/components/molecules/quote-comparison-table/comparison-side-drawer";
import CancellationCoverModal from "@/components/molecules/quote-section/quote-cover/modal/cancellation-cover-modal";
import BackToRenewal from "@/components/molecules/renewals/back-to-renewal/back-to-renewal";
import { OptimizelyExperimentsEnum, RenewalPage } from "@/config/app-constant";
import {
  ANNUAL_MULTI_TRIP,
  APPLICATION,
  CLASSIC,
  ESSENTIALS,
  PRODUCT,
  SINGLE_TRIP,
  VARIANT_FLOW,
} from "@/constants";
import useUpdateTripDetails, {
  UpdateProps,
} from "@/hooks/api/quote/update-trip-details";
import useGtmQuotePolicyOptions from "@/hooks/gtm/use-gtm-quote-policy-options";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { useLoading } from "@/providers/loading-provider";
import {
  BenefitCopyDataType,
  ComparisonTableAreaContentType,
  CancellationCovers,
  CoverDrawerDetailsDataType,
} from "@/schema/quote-page/page.schema";
import {
  CoverAreaDataTypeRenewal,
  CoverContentTypeRenewal,
  CoverDrawerDetailsDataTypeRenewal,
} from "@/schema/renewal-page/cover-level-page.schema";
import { RootState } from "@/store/root-reducer";
import {
  setCoverLevelChangedState,
  updateQuote,
} from "@/store/slices/quote/quote.slice";
import { QuoteData } from "@/types/quote/quote-data";
import isCruiseImpact from "@/utils/check-cruise-impact-base-on-brand.utils";
import QuoteCoverTypeMap from "@/utils/gtm/cover-type-map";
import QuoteDataMap from "@/utils/gtm/quote-data-map";
import handleError, { ErrorResponse } from "@/utils/handle-errors";

import getRelatedCoverData, {
  getComparisonTableDrawerDetailsData,
} from "./dynamic-data-map/magnolia-data-map";
import MapQuoteCard from "./dynamic-data-map/map-quote-card";
import SlickButton from "../../quote/quote-cover/slick-button/slick-button";

const mobileCardsClassNames = {
  start: "px-[6px] md:px-[0px] md:pl-[24px]",
  end: "px-[6px] md:px-[0px] md:pr-[24px]",
  middle: "md:px-[12px] px-[6px]",
};

const mobileCardsClassNamesForLessThreeCards = {
  start: "px-[6px] md:px-[0px] md:pl-[48px]",
  end: "px-[6px] md:px-[12px] md:pr-[48px]",
  middle: "",
};

function setPatchBody(
  attributes: QuoteData["attributes"],
  postData: UpdateProps["requestdata"]
) {
  let data = attributes.tripDetails;
  if (data) data = { ...data, ...postData };
  if (data?.policyType === SINGLE_TRIP) {
    delete data.area;
  }
  return data;
}

function QuoteCover({
  quoteCoverContent,
  coverLevelArea,
  drawerData,
  setScrolled,
  isUnsavedModalOpen = false,
  setIsUnsavedModalOpen,
}: Readonly<{
  quoteCoverContent: CoverContentTypeRenewal | null;
  drawerData?: CoverDrawerDetailsDataTypeRenewal | null;
  // drawerData?: any;
  coverLevelArea?: CoverAreaDataTypeRenewal;
  setScrolled?: (val: boolean) => void;
  isUnsavedModalOpen?: boolean;
  setIsUnsavedModalOpen?: (val: boolean) => void;
}>) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { hideLoading } = useLoading();
  const quoteData = useSelector((state: RootState) => state.quote);
  const { attributes, quoteCompleted, referenceId, version } = quoteData;

  const { tripDetails } = attributes;
  const wizardContext = useWizard();
  const currentPage = wizardContext.current;

  const [selectedCoverLevel, setSelectedCoverLevel] = useState("");
  const [updateCoverType, setUpdateCoverType] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const [isLoopComplete, setIsLoopComplete] = useState(false);
  const [dispSliderHaddleBtn, setDispSliderHaddleBtn] = useState(false);
  const isDesktopOriginal = useMediaQuery("(min-width: 904px)");
  const isDesktopABTest = useMediaQuery("(min-width: 1200px)");
  const isAbVariant = useMemo(() => {
    return (
      typeof window !== "undefined" &&
      localStorage.getItem(OptimizelyExperimentsEnum.RENEWAL_COVER_LEVEL) &&
      localStorage.getItem(OptimizelyExperimentsEnum.RENEWAL_COVER_LEVEL) ===
        VARIANT_FLOW &&
      APPLICATION === "renewal" &&
      PRODUCT === "staysure"
    );
  }, []);
  const isDesktop =
    (isDesktopOriginal && !isAbVariant) || (isDesktopABTest && isAbVariant);

  const [isOpen, setIsOpen] = useState(false);
  const [initiateGtmUpdate, setInitiateGtmUpdate] = useState(false);
  const [isChangeOpen, setIsChangeOpen] = useState(false); // cancelation Change
  const [dynamicCoverLimit, setDynamicCoverLimit] = useState<
    CancellationCovers[] | null
  >(null); // cancelation Change cover limits
  const [cancelltionChangeCoverType, setCancelltionChangeCoverType] =
    useState(""); // cancelation Change cover type
  const [totalSlides, setTotalSlides] = useState<number>(0);
  const [isOpenUnsavedChangesModal, setIsOpenUnsavedChangesModal] =
    useState(false);

  const isUnsavedChangesModalOpen = useMemo(() => {
    return isUnsavedModalOpen;
  }, [isUnsavedModalOpen]);

  let sliderRefer: any = useRef(null);
  const quoteCoverLevelsRef = useRef<HTMLDivElement>(null);
  const { current } = useWizard();
  // Get carousel config from definition or use defaults
  const extendedCarouselBreakpoint =
    Number(current.brandSpecificParams?.extendedCarouselBreakpoint) || 1165;
  const carouselPadding600to640 =
    typeof current.brandSpecificParams?.carouselCenterPadding600to640 ===
    "string"
      ? current.brandSpecificParams.carouselCenterPadding600to640
      : "10%";
  const isAboveExtendedBreakpoint = useMediaQuery(
    `(min-width: ${extendedCarouselBreakpoint}px)`
  );
  const shouldUseExtendedCarousel = useMemo(() => {
    return (
      PRODUCT === "staysure" &&
      APPLICATION === RenewalPage &&
      current.brandSpecificParams?.isDisplayWhichBestBuyBadge === true &&
      !isAboveExtendedBreakpoint
    );
  }, [
    current.brandSpecificParams?.isDisplayWhichBestBuyBadge,
    isAboveExtendedBreakpoint,
  ]);

  // Check if badge feature is enabled (for 600-640px carousel adjustment)
  const isBadgeFeatureEnabled = useMemo(() => {
    return (
      PRODUCT === "staysure" &&
      APPLICATION === "quote" &&
      current.brandSpecificParams?.isDisplayWhichBestBuyBadge === true
    );
  }, [current.brandSpecificParams?.isDisplayWhichBestBuyBadge]);

  const getCoverLevelData = (tripType: string) => {
    const dataList = getRelatedCoverData(
      coverLevelArea,
      tripDetails?.goingOnCruise && isCruiseImpact,
      tripType
    ).filter((item) => item !== null);
    if (dataList?.length && totalSlides === 0) {
      setTotalSlides(dataList?.length ?? 0);
    }
    return dataList;
  };

  const coverLevelItemData = useMemo(() => {
    return getCoverLevelData(tripDetails?.policyType ?? ANNUAL_MULTI_TRIP);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripDetails?.policyType]);

  useEffect(() => {
    if (tripDetails) {
      if (
        tripDetails.policyPackage &&
        tripDetails.initialQuotePageLoading === true
      ) {
        setSelectedCoverLevel("");
      } else if (tripDetails.policyPackage) {
        setSelectedCoverLevel(tripDetails.policyPackage);
      } else {
        setSelectedCoverLevel("");
      }
    }
  }, [tripDetails]);

  useEffect(() => {
    if (sliderRefer?.innerSlider?.slideHandler && autoPlay === false) {
      const selectedCoverLevelIndex = selectedCoverLevel
        ? coverLevelItemData.findIndex(
            (item) => item?.coverType === selectedCoverLevel
          )
        : Math.floor(totalSlides / 2) + (totalSlides % 2) - 1;
      sliderRefer?.innerSlider?.disableBodyScroll();
      setTimeout(() => {
        sliderRefer?.innerSlider?.slideHandler(selectedCoverLevelIndex);
        setDispSliderHaddleBtn(true);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, isDesktop]);

  useEffect(() => {
    if (updateCoverType === false) {
      dispatch(setCoverLevelChangedState(true));
    }

    if (
      updateCoverType &&
      sliderRefer?.innerSlider?.slideHandler &&
      !autoPlay
    ) {
      const selectedCoverLevelIndex = coverLevelItemData.findIndex(
        (item) => item?.coverType === selectedCoverLevel
      );
      sliderRefer?.innerSlider?.slideHandler(
        selectedCoverLevelIndex === -1
          ? Math.floor(totalSlides / 2) + (totalSlides % 2) - 1
          : selectedCoverLevelIndex
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCoverType]);

  const resetTrigger = useCallback(() => {
    setUpdateCoverType(false);
    if (setScrolled) setScrolled(true);
    hideLoading();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, loading, error, updateTripDetails } = useUpdateTripDetails({
    resetTrigger,
  });

  useEffect(() => {
    if (!loading && data && !error) {
      dispatch(
        updateQuote({
          ...data,
          quoteCompleted,
        })
      );
      setInitiateGtmUpdate(true);
    } else if (!loading && error !== null) {
      handleError(error as ApiError<ErrorResponse>, router, dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, data, dispatch]);

  let gtmQuoteData = null;
  let policyPackages = null;
  if (data) {
    gtmQuoteData = QuoteDataMap(
      data.attributes,
      "select_item",
      "cover selection",
      referenceId
    );
    policyPackages = QuoteCoverTypeMap(data.attributes);
    delete policyPackages[0]?.index;
  }
  delete gtmQuoteData?.ecommerce?.value;

  useGtmQuotePolicyOptions({
    quoteData: gtmQuoteData,
    policyItems: policyPackages,
    isReady: initiateGtmUpdate,
  });

  function enableScroll() {
    const body = document.getElementsByTagName("body")[0];
    body.style.overflow = "";
    body.style.top = "";
  }

  const comparionTableDynamicData =
    getComparisonTableDrawerDetailsData(drawerData);

  function disableScroll() {
    const scrollPosition = [
      window?.scrollX ||
        document.documentElement.scrollLeft ||
        document.body.scrollLeft,
      window?.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop,
    ];
    const body = document.getElementsByTagName("body")[0];
    body.style.top = `-${scrollPosition[1]}px`;
  }

  function updateSelectedCoverLevel(
    val: string,
    state: boolean,
    index: number
  ) {
    if (state && !updateCoverType) {
      setSelectedCoverLevel(state ? val : "");
      if (!isDesktop) {
        if (index === 0) {
          sliderRefer?.innerSlider?.slideHandler(0);
        } else if (index === 1) {
          sliderRefer?.innerSlider?.slideHandler(1);
        } else if (index === 2) {
          sliderRefer?.innerSlider?.slideHandler(2);
        }
      }
      const requestdata = {
        policyPackage: val,
        policyType: tripDetails?.policyType,
        initialQuotePageLoading: false,
      };

      const bodyData = setPatchBody(attributes, requestdata);

      updateTripDetails(bodyData);
      setUpdateCoverType(true);
      setInitiateGtmUpdate(false);
    }
  }

  const commonCoverLevelCardProps = useMemo(() => {
    return {
      selectedCoverLevel,
      selectedTab: tripDetails?.policyType,
      updateCoverType,
      setCancellationCoverOpen: setIsChangeOpen,
      setCancelltionChangeCoverType,
      setDynamicCoverLimit,
      updateSelectedCoverLevel,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCoverLevel, updateCoverType, version]);

  return (
    <div data-testid="QuoteCoverSection-test-id">
      {/* cancelation cover modal */}
      {isChangeOpen && (
        <CancellationCoverModal
          isOpen={isChangeOpen}
          setIsOpen={(val) => setIsChangeOpen(val)}
          values={dynamicCoverLimit || null}
          tripType={tripDetails?.policyType ?? ANNUAL_MULTI_TRIP}
          packageType={cancelltionChangeCoverType}
          cancelationCoverTitle={quoteCoverContent?.cancelationCoverTitle}
          cancelationCoverDescription={
            quoteCoverContent?.cancelationCoverDescription
          }
          cancelationCoverUpdateCta={
            quoteCoverContent?.cancelationCoverUpdateCta
          }
          cancelationCoverCancelCta={
            quoteCoverContent?.cancelationCoverCancelCta
          }
          placeholderText={quoteCoverContent?.cancellationDropdownLabel}
        />
      )}

      <div
        className="max-w-[1200px] lg:mx-auto mx-0 my-0 w-[100%] relative lg:bg-surface-neutral-option-1 
      lg:p-spacing-xxl lg:pt-spacing-xxl p-[0px] rounded-radius-xxl"
        ref={quoteCoverLevelsRef}
      >
        {quoteCoverContent?.isBackToDashboardCtaVisible && (
          <BackToRenewal
            label={quoteCoverContent?.backToRenewalCta ?? "back to renewals"}
            className="md:pb-[0px] 
          top-[-2px] !px-[0px] md:!mb-spacing-m mx-spacing-l md:mx-spacing-xxl lg:mx-[0px]"
            onClick={() => {
              if (tripDetails?.isPreviousCoverEssentials === true) {
                setIsOpenUnsavedChangesModal(true);
              } else {
                router.push(currentPage.edges?.backToRenewal ?? "/");
              }
            }}
          />
        )}

        {/*
          Extract the ternary logic into a variable before JSX
        */}
        {(() => {
          let setIsOpenHandler: (val: boolean) => void;
          if (isOpenUnsavedChangesModal) {
            setIsOpenHandler = setIsOpenUnsavedChangesModal;
          } else if (isUnsavedChangesModalOpen) {
            setIsOpenHandler = setIsUnsavedModalOpen ?? (() => {});
          } else {
            setIsOpenHandler = () => {};
          }
          return (
            <UnSavedChangesWarningMessage
              isOpen={isOpenUnsavedChangesModal || isUnsavedChangesModalOpen}
              onContinueClick={async () => {
                const result = await updateTripDetails(
                  setPatchBody(attributes, {
                    policyPackage: ESSENTIALS,
                    policyType: tripDetails?.policyType,
                    initialQuotePageLoading: false,
                    goingOnCruise:
                      tripDetails?.isPreviousCoverEssentials === true &&
                      tripDetails?.policyPackage === CLASSIC
                        ? false
                        : tripDetails?.goingOnCruise,
                  })
                );
                if (result) {
                  dispatch(updateQuote({ ...result, quoteCompleted }));
                  setIsOpenUnsavedChangesModal(false);
                  setIsUnsavedModalOpen?.(false);
                  router.push(currentPage.next ?? "/");
                }
              }}
              setIsOpen={setIsOpenHandler}
            />
          );
        })()}
        {/* desktop - exclude 904-1165px when extended carousel enabled */}
        {isDesktop && !shouldUseExtendedCarousel && (
          <div data-ab-id="cover-level-content-desktop-root">
            <div
              data-ab-id="cover-level-content-desktop-root-grid"
              className={`hidden lg:grid lg:grid-cols-${coverLevelItemData.length} gap-spacing-s items-start`}
            >
              {coverLevelItemData?.map((item, index) => {
                return (
                  <div key={index as unknown as string}>
                    <MapQuoteCard
                      {...quoteCoverContent}
                      {...commonCoverLevelCardProps}
                      type={index}
                      coverType={item?.coverType ?? ""}
                      coverTitle={item?.coverTitle ?? ""}
                      benifitData={
                        item?.benifitData as unknown as BenefitCopyDataType[]
                      }
                      closeDrawerCta={item?.closeDrawerCta}
                      coverDrawerTitle={item?.coverDrawerTitle}
                      coverDrawerCopy={item?.coverDrawerCopy}
                      coverDrawerDetails={
                        item?.coverDrawerDetails as unknown as CoverDrawerDetailsDataType[]
                      }
                      defaqtoRating={
                        item?.defaqtoRating as unknown as 1 | 2 | 3 | 4 | 5
                      }
                      isSocialProofChipActive={item?.isSocialProofChipActive}
                      chipTopText={item?.chipTopText ?? ""}
                      chipBottomText={item?.chipBottomText ?? ""}
                      whichBestBuyBadge={item?.whichBestBuyBadge}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* tablet & mobile - include 904-1165px when extended carousel enabled */}
        {(!isDesktop || shouldUseExtendedCarousel) && (
          <div
            className={`${
              shouldUseExtendedCarousel ? "" : "lg:hidden"
            } relative`}
            data-testid="tablet-and-mobile-slider"
          >
            <Slider
              className={` center`}
              ref={function (slider: any) {
                sliderRefer = slider;
              }}
              centerMode={totalSlides > 2}
              infinite={false}
              slidesToShow={totalSlides > 2 ? 1 : 2}
              initialSlide={0}
              autoplay={totalSlides > 2 ? autoPlay : false}
              speed={1000}
              autoplaySpeed={1000}
              arrows={false}
              verticalSwiping={false}
              variableWidth={!!isAbVariant || undefined}
              centerPadding={totalSlides > 2 ? "25%" : "0px"}
              accessibility
              afterChange={() => {
                if (isLoopComplete && totalSlides > 2) {
                  setAutoPlay(false);
                }
                enableScroll();
              }}
              beforeChange={(current: number, next: number) => {
                disableScroll();
                if (next === totalSlides - 2 && totalSlides > 2) {
                  setIsLoopComplete(true);
                }
              }}
              responsive={[
                // Conditionally include 640px breakpoint
                ...(isBadgeFeatureEnabled
                  ? [
                      {
                        breakpoint: 640,
                        settings: {
                          slidesToShow: 1,
                          centerMode: true,
                          verticalSwiping: false,
                          arrows: true,
                          autoplay: autoPlay,
                          nextArrow: (
                            <SlickButton
                              type="next"
                              btnDisplay={dispSliderHaddleBtn}
                              isCoverLevelSelected={selectedCoverLevel !== ""}
                            />
                          ),
                          prevArrow: (
                            <SlickButton
                              type="prev"
                              btnDisplay={dispSliderHaddleBtn}
                              isCoverLevelSelected={selectedCoverLevel !== ""}
                            />
                          ),
                          centerPadding: carouselPadding600to640,
                          afterChange: (current: number) => {
                            if (isLoopComplete || current === totalSlides - 1) {
                              setAutoPlay(false);
                            }
                            enableScroll();
                          },
                          beforeChange: (current: number, next: number) => {
                            disableScroll();
                            if (next === totalSlides - 2) {
                              setIsLoopComplete(true);
                            }
                          },
                        },
                      },
                    ]
                  : []),
                {
                  breakpoint: 600,
                  settings: {
                    slidesToShow: 1,
                    centerMode: true,
                    verticalSwiping: false,
                    arrows: true,
                    autoplay: autoPlay,
                    nextArrow: (
                      <SlickButton
                        type="next"
                        btnDisplay={dispSliderHaddleBtn}
                        isCoverLevelSelected={selectedCoverLevel !== ""}
                      />
                    ),
                    prevArrow: (
                      <SlickButton
                        type="prev"
                        btnDisplay={dispSliderHaddleBtn}
                        isCoverLevelSelected={selectedCoverLevel !== ""}
                      />
                    ),
                    centerPadding: "18px",
                    afterChange: (current) => {
                      if (isLoopComplete || current === totalSlides - 1) {
                        setAutoPlay(false);
                      }
                      enableScroll();
                    },
                    beforeChange: (current: number, next: number) => {
                      disableScroll();
                      if (next === totalSlides - 2) {
                        setIsLoopComplete(true);
                      }
                    },
                  },
                },
              ]}
            >
              {coverLevelItemData?.map((item, index) => {
                const cardPosition = (indexId: number) => {
                  if (indexId === 0) {
                    return "start";
                  }
                  if (indexId === totalSlides - 1) {
                    return "end";
                  }
                  return "middle";
                };

                return (
                  <div
                    key={index as unknown as string}
                    className={
                      totalSlides < 3
                        ? mobileCardsClassNamesForLessThreeCards[
                            cardPosition(index)
                          ]
                        : mobileCardsClassNames[cardPosition(index)]
                    }
                  >
                    <MapQuoteCard
                      {...quoteCoverContent}
                      {...commonCoverLevelCardProps}
                      type={index}
                      coverType={item?.coverType ?? ""}
                      coverTitle={item?.coverTitle ?? ""}
                      benifitData={
                        item?.benifitData as unknown as BenefitCopyDataType[]
                      }
                      closeDrawerCta={item?.closeDrawerCta}
                      coverDrawerTitle={item?.coverDrawerTitle}
                      coverDrawerCopy={item?.coverDrawerCopy}
                      coverDrawerDetails={
                        item?.coverDrawerDetails as unknown as CoverDrawerDetailsDataType[]
                      }
                      defaqtoRating={
                        item?.defaqtoRating as unknown as 1 | 2 | 3 | 4 | 5
                      }
                      isSocialProofChipActive={item?.isSocialProofChipActive}
                      chipTopText={item?.chipTopText}
                      chipBottomText={item?.chipBottomText}
                      whichBestBuyBadge={item?.whichBestBuyBadge}
                    />
                  </div>
                );
              })}
            </Slider>
          </div>
        )}

        <div className=" md:w-[265px] w-[280px] mx-auto ">
          <LabelButtonWrapper
            data-testid="showDetailedComparisonBtn"
            id="showDetailedComparisonBtn"
            type="button"
            hierarchy="secondary"
            label={quoteCoverContent?.detailedComparisonBtnLabel ?? ""}
            className="!border-[1.5px] text-[18px] leading-[18px] font-[600] bg-surface-neutral-option-1"
            onClick={() => {
              setIsOpen(true);
              document.getElementById("showDetailedComparisonBtn")?.blur();
            }}
          />
        </div>
      </div>
      <ComparisionSideDrawer
        drawerTitle={quoteCoverContent?.comparisonTableTitle ?? ""}
        comparisonTableCloseCta={
          quoteCoverContent?.comparisonTableCloseCta ?? "Close"
        }
        drawerData={
          comparionTableDynamicData as unknown as ComparisonTableAreaContentType
        }
        selectedTab={tripDetails?.policyType ?? ANNUAL_MULTI_TRIP}
        isOpen={isOpen}
        setOpen={setIsOpen}
      />
    </div>
  );
}

export default QuoteCover;
